/* All States Service: Fetches all states from the server, and provides States
 * to service clients on demand.
 *
 * Angular Module: trackerbase-app.services.all-states-service
 * Injected service name: AllStatesService
 */

import angular from "angular";
import Assert from "tbAssert";
import TbAppConfigDataServiceModule from "../config/tb-app-config/TbAppConfigDataService";

import State from "../models/State";

const mHttpSvc = new WeakMap();
const mQSvc = new WeakMap();
const mTbServerBaseUrl = new WeakMap();

class AllStatesService {
    constructor($http, $q, TbAppConfigDataService) {
        Assert.StrictAssert($http != null, "Assertion Failure: Missing Http Service Dependency");
        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert(TbAppConfigDataService != null, "Assertion Failure: Missing TrackerBase Application Configuration Data Service Dependency");

        mHttpSvc.set(this, $http);
        mQSvc.set(this, $q);
        mTbServerBaseUrl.set(this, TbAppConfigDataService.tbServerBaseUrl);

        this._AllStates = [];
        this.remoteLoadInProgress = false;
        this.remoteLoadPromise = null;
        this.remoteError = { errorOccurred: false, errorMessage: "" };
        this._StateIdMap = new Map();
        this._StateShortNameMap = new Map();
        this._StateFullNameMap = new Map();
    }

    __loadStateMaps() {
        this._StateIdMap.clear();
        this._StateShortNameMap.clear();
        this._StateFullNameMap.clear();
        this._AllStates.forEach(angular.bind(this, function(st) {
            this._StateIdMap.set(st.stateId, st);
            this._StateShortNameMap.set(st.shortName, st);
            this._StateFullNameMap.set(st.fullName, st);
        }));
    }

    loadRemoteStateData() {
        this.remoteLoadInProgress = true;
        const tbServerBaseUrl = mTbServerBaseUrl.get(this);

        this.remoteLoadPromise = mHttpSvc.get(this)({
            method: "GET",
            url: tbServerBaseUrl + "/api/states/all"
        }).then((response) => {
            if (response != null) {
                let responseData = response.data;
                if (responseData["Items"] != null) {
                    let processedStateData = responseData.Items.map(
                        function(serverStateItem) {
                            if (serverStateItem != null)
                            {
                                return State.FromServerState(serverStateItem);
                            }
                        }
                    );
                    this._AllStates.length = 0;
                    Array.prototype.push.apply(this._AllStates, processedStateData);
                    this.__loadStateMaps();
                    return true;
                }
                else {
                    return mQSvc.get(this).reject("Server Response Missing State Data Items Entry.");
                }
            }
            else {
                return mQSvc.get(this).reject("Invalid Server Response To State Data Request.");
            }
        }, (errorResponse) => {
            return mQSvc.get(this).reject(errorResponse);
        });

        return this.remoteLoadPromise.then(angular.bind(this, function(status) {
                if (status) {
                    this.remoteLoadInProgress = false;
                    return true;
                }
            }),
            angular.bind(this, function(errorResponse) {
                this.remoteError.errorOccurred = true;
                this.remoteError.errorMessage = errorResponse;
                this.remoteLoadInProgress = false;
            }));
    }

    GetStatesAsync(stateFilter) {
        let getStateDataPromise = null;
        if (this.remoteLoadInProgress) {
            getStateDataPromise = mQSvc.get(this).when(this.remoteLoadPromise).then((status) => {
                angular.noop(status);
                return this._AllStates;
            });
        }
        else if (this._AllStates.length < 40) {
            getStateDataPromise = this.loadRemoteStateData()
                .then((status) => {
                    angular.noop(status);
                    return this._AllStates;
                });
        }
        else {
            getStateDataPromise = mQSvc.get(this).when(this._AllStates);
        }

        if (stateFilter == null) {
            stateFilter = () => true;
        }

        getStateDataPromise = getStateDataPromise.then((stateData) => stateData.filter(stateFilter));

        return getStateDataPromise.then((stateData) => {
            let copiedData = stateData.map(function(stateObj) {
                return State.CopyState(stateObj);
            });
            return copiedData;

        }, (errorResponse) => {
            mQSvc.get(this).reject(errorResponse);
        });
    }

    __GetSingleStateAsync(stateFilter) {
        let stateDataPromise = this.GetStates(stateFilter).then(
            (stateSearchResults) => {
                if (stateSearchResults.length === 1) {
                    return stateSearchResults[0];
                } else {
                    return mQSvc.get(this).reject("Invalid or missing state id.");
                }
            });

        return stateDataPromise;
    }

    FromId(stateId) {
        let result = this._StateIdMap.get(stateId);
        if (result != null)  {
            return State.CopyState(result);
        }

        return result;
    }

    FromIdAsync(stateId) {
        let stateIdFilter = (element) => (element["stateId"] === stateId);
        return this.__GetSingleStateAsync(stateIdFilter);
    }

    FromShortName(shortName) {
        let result = this._StateShortNameMap.get(shortName);
        if (result != null) {
            return State.CopyState(result);
        }

        return result;
    }

    FromShortNameAsync(shortName) {
        let shortNameStateFilter = (element) => (element["shortName"] === shortName);
        return this.__GetSingleStateAsync(shortNameStateFilter);
    }

    FromFullName(fullName) {
        let result = this._StateFullNameMap.get(fullName);
        if (result != null) {
            return State.CopyState(result);
        }

        return result;
    }

    FromFullNameAsync(fullName) {
        let fullNameStateFilter = (element) => (element["fullName"] === fullName);
        return this.__GetSingleStateAsync(fullNameStateFilter);
    }

    GetAllActualStatesAsync() {
        let actualStateFilter = (element) => (element.isActualState());
        return this.GetStatesAsync(actualStateFilter);
    }

    GetAllRealStatesAsync() {
        let realStateFilter = (element) => (element.isRealState());
        return this.GetStatesAsync(realStateFilter);
    }
}

AllStatesService.$inject = [ "$http", "$q", "TbAppConfigDataService" ];

export default angular
    .module("trackerbase-app.services.all-states-service", [
        TbAppConfigDataServiceModule
    ])
    .service("AllStatesService", AllStatesService)
    .name;



//////////////////
// WEBPACK FOOTER
// ./src/services/AllStatesService.js
// module id = DoPs
// module chunks = 1